// Pointage d'un rendez-vous par le service : présent, absent (motif facultatif), ou report sur un
// nouveau créneau choisi parmi les créneaux libres de l'agenda (semaine au choix).
import { Bandeau, Bouton, DialogueConfirmation, Selection, ZoneTexte } from '@cc/ui';
import { useState } from 'react';
import { requete } from '../../api/client';
import { useAction } from '../../commun/useAction';
import { decalerJours, heure, libelleJour, lundiLocal, useCreneaux, type RendezVous } from './donnees';
import commun from '../../commun/commun.module.css';
import styles from './rendez-vous.module.css';

export function PointageRendezVous({ rdv, compact }: { rdv: RendezVous; compact?: boolean }) {
  const [dialogue, setDialogue] = useState<'absent' | 'reporte' | null>(null);
  const [motif, setMotif] = useState('');
  const [semaine, setSemaine] = useState(lundiLocal(rdv.debut.slice(0, 10)));
  const [nouveau, setNouveau] = useState('');
  const creneaux = useCreneaux(dialogue === 'reporte' ? rdv.agenda.id : null, semaine);
  const invalider = [['rendez-vous'], ['creneaux'], ['indicateurs']];
  const fermer = () => {
    setDialogue(null);
    setMotif('');
    setNouveau('');
  };
  const constat = useAction({
    executer: (corps: { etat: 'present' | 'absent' | 'reporte'; motif?: string; nouveau_debut?: string }) => requete(`/rendez-vous/${rdv.id}/etat`, { methode: 'POST', corps }),
    succes: (_r, e) => (e.etat === 'present' ? 'Présence pointée.' : e.etat === 'absent' ? 'Absence pointée.' : 'Rendez-vous reporté : l\'usager est informé du nouveau créneau.'),
    invalider,
    apres: fermer,
  });
  const actif = rdv.etat === 'confirme' || rdv.etat === 'reserve';
  if (!actif) return null;
  return (
    <div className={styles.pointage}>
      <Bouton petit={compact} variante="principal" chargement={constat.enCours} onClick={() => constat.lancer({ etat: 'present' })} aria-label={`Pointer présent : ${rdv.demandeur.nom ?? 'usager'} à ${heure(rdv.debut)}`}>
        Présent
      </Bouton>
      <Bouton petit={compact} variante="secondaire" onClick={() => setDialogue('absent')} aria-label={`Pointer absent : ${rdv.demandeur.nom ?? 'usager'} à ${heure(rdv.debut)}`}>
        Absent
      </Bouton>
      <Bouton petit={compact} variante="discret" onClick={() => setDialogue('reporte')} aria-label={`Reporter : ${rdv.demandeur.nom ?? 'usager'} à ${heure(rdv.debut)}`}>
        Reporter
      </Bouton>
      {constat.messageErreur && !dialogue ? <span className={commun.na}>{constat.messageErreur}</span> : null}

      <DialogueConfirmation ouvert={dialogue === 'absent'} titre="Pointer l'usager absent" libelleConfirmer="Pointer absent" variante="secondaire" enCours={constat.enCours} onConfirmer={() => constat.lancer({ etat: 'absent', motif: motif.trim() || undefined })} onAnnuler={fermer} message={<p>{rdv.demandeur.nom ?? 'L\'usager'}, {libelleJour(rdv.debut.slice(0, 10))} à {heure(rdv.debut)}, {rdv.agenda.libelle}.</p>}>
        <ZoneTexte libelle="Motif" value={motif} facultatif rows={2} onChange={(e) => setMotif(e.target.value)} />
        {constat.messageErreur ? <Bandeau type="erreur">{constat.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>

      <DialogueConfirmation ouvert={dialogue === 'reporte'} titre="Reporter le rendez-vous" libelleConfirmer="Reporter sur ce créneau" enCours={constat.enCours} confirmationDesactivee={!nouveau} onConfirmer={() => constat.lancer({ etat: 'reporte', nouveau_debut: nouveau, motif: motif.trim() || undefined })} onAnnuler={fermer} message={<p>Un nouveau rendez-vous lié est créé sur le créneau choisi ; l'usager est prévenu.</p>}>
        <div className={commun.ligne}>
          <Bouton petit variante="secondaire" onClick={() => setSemaine(decalerJours(semaine, -7))}>
            Semaine précédente
          </Bouton>
          <span>Semaine du {libelleJour(semaine)}</span>
          <Bouton petit variante="secondaire" onClick={() => setSemaine(decalerJours(semaine, 7))}>
            Semaine suivante
          </Bouton>
        </div>
        <Selection
          libelle="Nouveau créneau"
          value={nouveau}
          optionVide={creneaux.isPending ? 'Chargement des créneaux' : (creneaux.data?.creneaux.length ?? 0) === 0 ? 'Aucun créneau libre cette semaine' : 'Choisir un créneau'}
          options={(creneaux.data?.creneaux ?? []).filter((c) => c.places_restantes > 0).map((c) => ({ valeur: c.debut, libelle: `${libelleJour(c.debut.slice(0, 10))} ${heure(c.debut)} (${c.places_restantes} place${c.places_restantes > 1 ? 's' : ''})` }))}
          onChange={(e) => setNouveau(e.target.value)}
        />
        <ZoneTexte libelle="Motif du report" value={motif} facultatif rows={2} onChange={(e) => setMotif(e.target.value)} />
        {creneaux.error ? <Bandeau type="erreur">Créneaux non disponibles : la collectivité n'expose pas d'agenda public.</Bandeau> : null}
        {constat.messageErreur ? <Bandeau type="erreur">{constat.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>
    </div>
  );
}
